import React, { FC, useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles';

import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import EditorContext from './EditorContext'
import projectSelectors from '../../store/selectors/project'
import {
  runGenericHook,
} from '@kaidam/fc-shared/src/utils/hooks'

import {
  ILayout_Cell_Type,
  ILayout_Options,
} from '@kaidam/fc-shared/src/types/layout'

import {
  LAYOUT_CELL_TYPE_EMPTY,
  LAYOUT_CELL_TYPE_TEXT,
  LAYOUT_CELL_TYPE_IMAGE,
  LAYOUT_CELL_TYPE_VIDEO,
  LAYOUT_CELL_TYPE_FILE,
  LAYOUT_CELL_TYPE_TABLE,
  LAYOUT_CELL_TYPE_HTML,
  LAYOUT_CELL_TYPE_BOOKING_DETAILS,
} from '@kaidam/fc-shared/src/constants'

import { useEditor } from '@craftjs/core'
import icons from '../../icons'

import Cell from './Cell'

const TextIcon = icons.text
const ImageIcon = icons.image
const VideoIcon = icons.video
const SpacerIcon = icons.spacer
const FileIcon = icons.file
const TableIcon = icons.grid
const HTMLIcon = icons.code
const CalendarIcon = icons.calendar

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    backgroundColor: '#f5f5f5',   
  },
  cardHeader: {
    padding: theme.spacing(1),
  },
  avatar: {
    width: '30px',
    height: '30px',
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
  },
  icon: {
    fontSize: '0.8em',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}))

interface LibraryProps {
  
}

const Library: FC<React.PropsWithChildren<LibraryProps>> = ({
  
}) => {

  const { connectors } = useEditor()
  const classes = useStyles()
  const editorContext = useContext(EditorContext)
  const summary = useSelector(projectSelectors.summary)
  const CellAny = Cell as any

  const addElement = useCallback((type: ILayout_Cell_Type, data: any) => {
    const handler = (ref: HTMLDivElement | null) => {
      connectors.create(ref, (
        <CellAny
          isNew
          type={ type }
          data={ data }
          options={ editorContext.options }
        />
      ))
    }
    return handler
  }, [
    editorContext.options,
  ])

  const widgets = useMemo(() => {
    const useOptions = runGenericHook(summary, 'mapLayoutOptions', editorContext.options)
    return useOptions.widgets || []
  }, [
    editorContext.options,
    summary,
  ])

  return (
    <div className={ classes.root }>
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_TEXT) >= 0 && (
          <Tooltip title="Text">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_TEXT, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <TextIcon className={ classes.icon } />
                  </Avatar>
                }
                title="Text"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_IMAGE) >= 0 && (
          <Tooltip title="Image">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_IMAGE, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <ImageIcon className={ classes.icon } />
                  </Avatar>
                }
                title="Image"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_VIDEO) >= 0 && (
          <Tooltip title="Video">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_VIDEO, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <VideoIcon className={ classes.icon } />
                  </Avatar>
                }
                title="Video"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_FILE) >= 0 && (
          <Tooltip title="File">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_FILE, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <FileIcon className={ classes.icon } />
                  </Avatar>
                }
                title="File"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_TABLE) >= 0 && (
          <Tooltip title="Table">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_TABLE, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <TableIcon className={ classes.icon } />
                  </Avatar>
                }
                title="Table"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_HTML) >= 0 && (
          <Tooltip title="HTML">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_HTML, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <HTMLIcon className={ classes.icon } />
                  </Avatar>
                }
                title="HTML"
              />
            </Card>
          </Tooltip>
        )
      }
      {
        widgets.indexOf(LAYOUT_CELL_TYPE_BOOKING_DETAILS) >= 0 && (
          <Tooltip title="Booking Details">
            <Card
              className={ classes.card }
              ref={ addElement(LAYOUT_CELL_TYPE_BOOKING_DETAILS, {}) }
            >
              <CardHeader
                className={ classes.cardHeader }
                avatar={
                  <Avatar className={ classes.avatar }>
                    <CalendarIcon className={ classes.icon } />
                  </Avatar>
                }
                title="Booking Details"
              />
            </Card>
          </Tooltip>
        )
      }
      <Tooltip title="Spacer">
        <Card
          className={ classes.card }
          ref={ addElement('spacer', {}) }
        >
          <CardHeader
            className={ classes.cardHeader }
            avatar={
              <Avatar className={ classes.avatar }>
                <SpacerIcon className={ classes.icon } />
              </Avatar>
            }
            title="Spacer"
          />
        </Card>
      </Tooltip>
      <Divider className={ classes.divider } />
      <Typography variant="caption">
        click and drag these items onto your layout...
      </Typography>
    </div>
  )

}

export default Library